// Calendly: event types, project mapping, availability, booking intents (PRD §12).
import { z } from 'zod';
import * as C from './api-calendar.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

export const calendarRoutes = {
  listCalendarConnections: route({
    method: 'GET', path: '/workspaces/:workspaceId/calendar/connections', summary: 'Conexiones de Calendly (predeterminada, webhooks, tipos de evento)', tag: 'calendar',
    minRole: 'advisor', params: Ws, response: items(C.CalendarConnectionSchema),
  }),
  setDefaultCalendarConnection: route({
    method: 'POST', path: '/workspaces/:workspaceId/calendar/connections/:connectionId/default', summary: 'Marcar como Calendly predeterminado de la organización', tag: 'calendar',
    minRole: 'admin', params: W({ connectionId: IdParam }), response: items(C.CalendarConnectionSchema),
  }),
  syncEventTypes: route({
    method: 'POST', path: '/workspaces/:workspaceId/calendar/connections/:connectionId/event-types/sync', summary: 'Sincronizar tipos de evento desde Calendly', tag: 'calendar',
    minRole: 'admin', params: W({ connectionId: IdParam }), response: C.SyncEventTypesResultSchema,
  }),
  createCalendlyWebhook: route({
    method: 'POST', path: '/workspaces/:workspaceId/calendar/connections/:connectionId/webhook', summary: 'Crear suscripción de webhooks en Calendly', tag: 'calendar',
    minRole: 'admin', params: W({ connectionId: IdParam }), body: C.CreateCalendlyWebhookSchema, response: C.CalendarConnectionSchema,
  }),
  getProjectCalendar: route({
    method: 'GET', path: '/workspaces/:workspaceId/projects/:projectId/calendar', summary: 'Conexión efectiva y tipos de evento del proyecto', tag: 'calendar',
    minRole: 'advisor', params: W({ projectId: IdParam }), response: C.ProjectCalendarSchema,
  }),
  updateProjectEventTypes: route({
    method: 'PUT', path: '/workspaces/:workspaceId/projects/:projectId/calendar/event-types', summary: 'Tipos de evento permitidos del proyecto', tag: 'calendar',
    minRole: 'admin', params: W({ projectId: IdParam }), body: C.UpdateProjectEventTypesSchema, response: C.ProjectCalendarSchema,
  }),
  getAvailability: route({
    method: 'GET', path: '/workspaces/:workspaceId/projects/:projectId/calendar/availability', summary: 'Horarios disponibles reales (Calendly)', tag: 'calendar',
    minRole: 'advisor', params: W({ projectId: IdParam }), query: C.AvailabilityQuerySchema, response: C.AvailabilitySchema,
  }),
  runTestBooking: route({
    method: 'POST', path: '/workspaces/:workspaceId/calendar/test-booking', summary: 'Reserva de prueba real (se cancela al terminar)', tag: 'calendar',
    minRole: 'admin', params: Ws, body: C.TestBookingSchema, response: C.TestBookingResultSchema,
  }),
  createBooking: route({
    method: 'POST', path: '/workspaces/:workspaceId/bookings', summary: 'Agendar visita (intención idempotente, confirmada solo por Calendly)', tag: 'calendar',
    minRole: 'advisor', params: Ws, body: C.CreateBookingSchema, response: C.BookingResultSchema,
  }),
  listBookingIntents: route({
    method: 'GET', path: '/workspaces/:workspaceId/booking-intents', summary: 'Intenciones de reserva que requieren conciliación', tag: 'calendar',
    minRole: 'advisor', params: Ws, query: C.BookingIntentListQuerySchema, response: items(C.BookingIntentSchema),
  }),
  reconcileBookingIntent: route({
    method: 'POST', path: '/workspaces/:workspaceId/booking-intents/:intentId/reconcile', summary: 'Conciliar ahora con Calendly (sin volver a agendar)', tag: 'calendar',
    minRole: 'advisor', params: W({ intentId: IdParam }), response: C.BookingResultSchema,
  }),
  getAppointment: route({
    method: 'GET', path: '/workspaces/:workspaceId/appointments/:appointmentId', summary: 'Detalle de cita con historial', tag: 'appointments',
    minRole: 'advisor', params: W({ appointmentId: IdParam }), response: C.AppointmentDetailSchema,
  }),
  cancelAppointment: route({
    method: 'POST', path: '/workspaces/:workspaceId/appointments/:appointmentId/cancel', summary: 'Cancelar en Calendly (solo citas individuales)', tag: 'appointments',
    minRole: 'advisor', params: W({ appointmentId: IdParam }), body: C.CancelAppointmentSchema, response: C.AppointmentDetailSchema,
  }),
  requestReschedule: route({
    method: 'POST', path: '/workspaces/:workspaceId/appointments/:appointmentId/reschedule', summary: 'Solicitar reprogramación (enlace de Calendly o tarea de asesor)', tag: 'appointments',
    minRole: 'advisor', params: W({ appointmentId: IdParam }), body: C.RequestRescheduleSchema, response: C.RescheduleResultSchema,
  }),

  // Machine route: Calendly → us. Authenticated by the Calendly-Webhook-Signature header, never cookies.
  calendlyWebhook: route({
    method: 'POST', path: '/providers/calendly/:connectionId/webhooks', summary: 'Webhook de Calendly (firmado)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: C.CalendlyWebhookAckSchema,
  }),
  calcomWebhook: route({
    method: 'POST', path: '/providers/calcom/:connectionId/webhooks', summary: 'Webhook de Cal.com (firmado con x-cal-signature-256)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: C.CalendlyWebhookAckSchema,
  }),
} as const;
